import { useEffect, useState } from 'react';
import { 
  Globe, Search, FileText, Code, RefreshCw, ExternalLink,
  CheckCircle, AlertTriangle, Copy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminSEO = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(prodRes.data || []);
      setCategories(catRes.data.categories || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSeoMeta = async (pageType, entityId = null) => {
    try {
      const params = entityId ? `?entity_id=${entityId}` : '';
      const response = await axios.get(`${API}/seo/meta/${pageType}${params}`);
      setSeoData(response.data);
      setFormData({
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || '',
        meta_keywords: response.data.meta_keywords || [],
        og_title: response.data.og_title || '',
        og_description: response.data.og_description || '',
        og_image: response.data.og_image || '',
        canonical_url: response.data.canonical_url || ''
      });
    } catch (error) {
      console.error('Error loading SEO meta:', error);
    }
  };

  const handleSaveMeta = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/seo/meta`, {
        page_type: selectedEntity?.type || 'page',
        entity_id: selectedEntity?.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO metadata saved successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Error saving SEO meta:', error);
      toast.error('Failed to save SEO metadata');
    }
  };

  const selectEntity = (type, id, name) => {
    setSelectedEntity({ type, id, name });
    loadSeoMeta(type, id);
    setEditMode(false);
  };

  const copySitemapUrl = () => {
    navigator.clipboard.writeText(`${BACKEND_URL}/api/sitemap.xml`);
    toast.success('Sitemap URL copied!');
  };

  const copyRobotsUrl = () => {
    navigator.clipboard.writeText(`${BACKEND_URL}/api/robots.txt`);
    toast.success('Robots.txt URL copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-seo">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <Globe className="w-6 h-6 text-gold" />
            SEO Settings
          </h1>
          <p className="text-charcoal-light mt-1">Optimize your store for search engines</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-charcoal flex items-center gap-2">
              <FileText className="w-5 h-5 text-gold" />
              Sitemap
            </h3>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-sm text-charcoal-light mb-3">Auto-generated XML sitemap</p>
          <div className="flex gap-2">
            <button
              onClick={copySitemapUrl}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 border border-charcoal/20 text-sm text-charcoal hover:bg-beige"
            >
              <Copy className="w-3 h-3" /> Copy URL
            </button>
            <a
              href={`${BACKEND_URL}/api/sitemap.xml`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 border border-charcoal/20 text-sm text-charcoal hover:bg-beige"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-charcoal flex items-center gap-2">
              <Code className="w-5 h-5 text-gold" />
              Robots.txt
            </h3>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-sm text-charcoal-light mb-3">Search engine crawler directives</p>
          <div className="flex gap-2">
            <button
              onClick={copyRobotsUrl}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 border border-charcoal/20 text-sm text-charcoal hover:bg-beige"
            >
              <Copy className="w-3 h-3" /> Copy URL
            </button>
            <a
              href={`${BACKEND_URL}/api/robots.txt`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 border border-charcoal/20 text-sm text-charcoal hover:bg-beige"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="bg-white border border-charcoal/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-charcoal flex items-center gap-2">
              <Search className="w-5 h-5 text-gold" />
              Schema Markup
            </h3>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-sm text-charcoal-light mb-3">JSON-LD structured data</p>
          <p className="text-xs text-green-600">Auto-generated for products</p>
        </div>
      </div>

      {/* Meta Editor */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Entity Selector */}
        <div className="bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10">
            <h3 className="font-medium text-charcoal">Select Page</h3>
          </div>
          <div className="divide-y divide-charcoal/5 max-h-96 overflow-y-auto">
            <button
              onClick={() => selectEntity('homepage', null, 'Homepage')}
              className={`w-full p-3 text-left text-sm hover:bg-beige ${
                selectedEntity?.type === 'homepage' ? 'bg-gold/10 text-gold' : 'text-charcoal'
              }`}
            >
              Homepage
            </button>
            
            <div className="p-2 text-xs text-charcoal-light uppercase tracking-wider bg-beige/50">
              Categories
            </div>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => selectEntity('category', cat.id, cat.name)}
                className={`w-full p-3 text-left text-sm hover:bg-beige ${
                  selectedEntity?.id === cat.id ? 'bg-gold/10 text-gold' : 'text-charcoal'
                }`}
              >
                {cat.name}
              </button>
            ))}
            
            <div className="p-2 text-xs text-charcoal-light uppercase tracking-wider bg-beige/50">
              Products ({products.length})
            </div>
            {products.slice(0, 20).map((prod) => (
              <button
                key={prod.id}
                onClick={() => selectEntity('product', prod.id, prod.name)}
                className={`w-full p-3 text-left text-sm hover:bg-beige truncate ${
                  selectedEntity?.id === prod.id ? 'bg-gold/10 text-gold' : 'text-charcoal'
                }`}
              >
                {prod.name}
              </button>
            ))}
          </div>
        </div>

        {/* Meta Editor */}
        <div className="md:col-span-2 bg-white border border-charcoal/10">
          <div className="p-4 border-b border-charcoal/10 flex items-center justify-between">
            <h3 className="font-medium text-charcoal">
              {selectedEntity ? `Meta for: ${selectedEntity.name}` : 'Select a page to edit'}
            </h3>
            {selectedEntity && (
              <button
                onClick={() => setEditMode(!editMode)}
                className="px-3 py-1 text-sm text-gold hover:bg-gold/10"
              >
                {editMode ? 'Cancel' : 'Edit'}
              </button>
            )}
          </div>
          
          {selectedEntity ? (
            <div className="p-4 space-y-4">
              {/* Preview */}
              <div className="bg-beige/30 p-4 rounded">
                <p className="text-xs text-charcoal-light mb-1">Search Preview</p>
                <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                  {formData.meta_title || seoData?.meta_title || 'Page Title'}
                </p>
                <p className="text-green-700 text-sm">
                  jatfashionhouse.com › {selectedEntity.type} › ...
                </p>
                <p className="text-sm text-charcoal-light line-clamp-2">
                  {formData.meta_description || seoData?.meta_description || 'Meta description will appear here...'}
                </p>
              </div>

              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Meta Title</label>
                    <input
                      type="text"
                      value={formData.meta_title}
                      onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                      className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                      maxLength={60}
                    />
                    <p className="text-xs text-charcoal-light mt-1">{formData.meta_title.length}/60 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">Meta Description</label>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                      maxLength={160}
                    />
                    <p className="text-xs text-charcoal-light mt-1">{formData.meta_description.length}/160 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">OG Title (Social)</label>
                    <input
                      type="text"
                      value={formData.og_title}
                      onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                      className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1">OG Image URL</label>
                    <input
                      type="url"
                      value={formData.og_image}
                      onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    />
                  </div>

                  <button
                    onClick={handleSaveMeta}
                    className="w-full px-4 py-2 bg-gold text-white hover:bg-gold-dark"
                  >
                    Save Meta Data
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-charcoal-light">Meta Title</p>
                    <p className="text-sm text-charcoal">{seoData?.meta_title || 'Auto-generated'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-charcoal-light">Meta Description</p>
                    <p className="text-sm text-charcoal">{seoData?.meta_description || 'Auto-generated'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-charcoal-light">OG Title</p>
                    <p className="text-sm text-charcoal">{seoData?.og_title || 'Same as meta title'}</p>
                  </div>
                  {seoData?.schema_markup && (
                    <div>
                      <p className="text-xs text-charcoal-light">Schema Type</p>
                      <p className="text-sm text-charcoal">{seoData.schema_type || 'WebSite'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-charcoal-light">
              <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select a page from the left to view and edit SEO settings</p>
            </div>
          )}
        </div>
      </div>

      {/* SEO Tips */}
      <div className="bg-gold/5 border border-gold/20 p-4">
        <h3 className="font-medium text-charcoal mb-3">SEO Best Practices</h3>
        <ul className="grid md:grid-cols-2 gap-2 text-sm text-charcoal-light">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
            Keep meta titles under 60 characters
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
            Meta descriptions should be 150-160 characters
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
            Include primary keywords naturally
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
            Use unique descriptions for each page
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
            Schema markup helps rich snippets in search
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
            Sitemap submitted to Google Search Console
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminSEO;
